import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp,
  deleteField,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Logo } from '../../types';

const LOGOS_COLLECTION = 'logos';
const LOGOS_PER_PAGE = 50; // Load 50 logos at a time

interface LogosQueryResult {
  logos: Logo[];
  hasMore: boolean;
  lastDoc?: DocumentSnapshot;
  total: number;
}

interface LogosQueryOptions {
  pageSize?: number;
  lastDoc?: DocumentSnapshot;
  searchTerm?: string;
  selectedTags?: string[];
  tab?: 'available' | 'claimed' | 'deleted';
}

export const logoService = {
  // Get logos with pagination and filtering
  async getLogos(options: LogosQueryOptions = {}): Promise<LogosQueryResult> {
    try {
      const {
        pageSize = LOGOS_PER_PAGE,
        lastDoc,
        searchTerm = '',
        selectedTags = [],
        tab = 'available'
      } = options;

      // Build the base query
      let q = query(collection(db, LOGOS_COLLECTION));

      // Add tab-based filtering AND exclude permanently deleted logos
      if (tab === 'available') {
        q = query(q, where('claimed', '==', false), where('deleted', '==', false));
      } else if (tab === 'claimed') {
        q = query(q, where('claimed', '==', true), where('deleted', '==', false));
      } else if (tab === 'deleted') {
        q = query(q, where('deleted', '==', true));
      }

      // Add tag filtering (server-side)
      if (selectedTags.length > 0) {
        // Note: Firestore array-contains-any supports up to 10 values
        const tagsToFilter = selectedTags.slice(0, 10);
        q = query(q, where('tags', 'array-contains-any', tagsToFilter));
      }

      // Add ordering and pagination
      q = query(q, orderBy('createdAt', 'desc'));
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      q = query(q, limit(pageSize + 1)); // +1 to check if there are more

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      
      // Check if there are more documents
      const hasMore = docs.length > pageSize;
      const logosToReturn = hasMore ? docs.slice(0, pageSize) : docs;
      
      const logos = logosToReturn.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        claimedDate: doc.data().claimedDate?.toDate() || undefined,
      })) as Logo[];

      // Filter out permanently deleted logos at the JavaScript level
      const filteredLogos = logos.filter(logo => !logo.permanentlyDeleted);

      // Filter by search term client-side (for now)
      // TODO: Move to server-side search with Algolia for better performance
      let searchFilteredLogos = filteredLogos;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        searchFilteredLogos = filteredLogos.filter(logo =>
          logo.name.toLowerCase().includes(searchLower) ||
          logo.project.toLowerCase().includes(searchLower) ||
          logo.tags.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }

      return {
        logos: searchFilteredLogos,
        hasMore: hasMore && searchFilteredLogos.length === pageSize,
        lastDoc: hasMore ? logosToReturn[logosToReturn.length - 1] : undefined,
        total: searchFilteredLogos.length // This is approximate when paginated
      };
    } catch (error) {
      console.error('Error fetching logos:', error);
      throw error;
    }
  },

  // Get all logos (legacy method - kept for backward compatibility)
  async getAllLogos(): Promise<Logo[]> {
    try {
      const q = query(collection(db, LOGOS_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const logos = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        claimedDate: doc.data().claimedDate?.toDate() || undefined,
      })) as Logo[];

      // Filter out permanently deleted logos
      return logos.filter(logo => !logo.permanentlyDeleted);
    } catch (error) {
      console.error('Error fetching all logos:', error);
      throw error;
    }
  },

  // Get counts for tabs (optimized query) - exclude permanently deleted
  async getTabCounts(): Promise<{ available: number; claimed: number; deleted: number }> {
    try {
      // Get all logos and filter in JavaScript to exclude permanently deleted
      const allLogosSnapshot = await getDocs(collection(db, LOGOS_COLLECTION));
      const allLogos = allLogosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Logo[];

      // Filter out permanently deleted logos
      const activeLogos = allLogos.filter(logo => !logo.permanentlyDeleted);

      // Count by status
      const available = activeLogos.filter(logo => !logo.claimed && !logo.deleted).length;
      const claimed = activeLogos.filter(logo => logo.claimed && !logo.deleted).length;
      const deleted = activeLogos.filter(logo => logo.deleted).length;

      return { available, claimed, deleted };
    } catch (error) {
      console.error('Error fetching tab counts:', error);
      // Return zeros if error
      return { available: 0, claimed: 0, deleted: 0 };
    }
  },

  // Get all unique tags (for filter dropdown) - exclude permanently deleted
  async getAllTags(): Promise<string[]> {
    try {
      const q = query(collection(db, LOGOS_COLLECTION));
      const querySnapshot = await getDocs(q);
      
      const allTags = new Set<string>();
      querySnapshot.docs.forEach(doc => {
        const logo = doc.data() as Logo;
        // Skip permanently deleted logos
        if (!logo.permanentlyDeleted && !logo.deleted) {
          logo.tags?.forEach(tag => allTags.add(tag));
        }
      });
      
      return Array.from(allTags).sort();
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  },

  // Add a new logo
  async addLogo(logoData: Omit<Logo, 'id' | 'createdAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, LOGOS_COLLECTION), {
        ...logoData,
        createdAt: Timestamp.now(),
        claimedDate: logoData.claimedDate ? Timestamp.fromDate(logoData.claimedDate) : null,
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding logo:', error);
      throw error;
    }
  },

  // Update logo (for claiming/releasing)
  async updateLogo(logoId: string, updates: Partial<Logo>): Promise<void> {
    try {
      const logoRef = doc(db, LOGOS_COLLECTION, logoId);
      const updateData = { ...updates };
      
      // Convert dates to Timestamps
      if (updateData.claimedDate) {
        updateData.claimedDate = Timestamp.fromDate(updateData.claimedDate) as any;
      }
      
      await updateDoc(logoRef, updateData);
    } catch (error) {
      console.error('Error updating logo:', error);
      throw error;
    }
  },

  // Claim a logo
  async claimLogo(logoId: string, userEmail: string): Promise<void> {
    return this.updateLogo(logoId, {
      claimed: true,
      claimedBy: userEmail,
      claimedDate: new Date(),
    });
  },

  // Release a logo
  async releaseLogo(logoId: string): Promise<void> {
    try {
      const logoRef = doc(db, LOGOS_COLLECTION, logoId);
      await updateDoc(logoRef, {
        claimed: false,
        claimedBy: deleteField(),
        claimedDate: deleteField(),
      });
    } catch (error) {
      console.error('Error releasing logo:', error);
      throw error;
    }
  },

  // Soft delete a logo
  async deleteLogo(logoId: string, userEmail: string): Promise<void> {
    try {
      const logoRef = doc(db, LOGOS_COLLECTION, logoId);
      await updateDoc(logoRef, {
        deleted: true,
        deletedBy: userEmail,
        deletedDate: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error deleting logo:', error);
      throw error;
    }
  },

  // Restore a deleted logo
  async restoreLogo(logoId: string): Promise<void> {
    try {
      const logoRef = doc(db, LOGOS_COLLECTION, logoId);
      await updateDoc(logoRef, {
        deleted: false,
        deletedBy: deleteField(),
        deletedDate: deleteField(),
      });
    } catch (error) {
      console.error('Error restoring logo:', error);
      throw error;
    }
  },

  // Permanently delete a logo (admin/owner only)
  async permanentlyDeleteLogo(logoId: string): Promise<void> {
    try {
      const logoRef = doc(db, LOGOS_COLLECTION, logoId);
      await updateDoc(logoRef, {
        permanentlyDeleted: true,
        permanentlyDeletedDate: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error permanently deleting logo:', error);
      throw error;
    }
  },
};